import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AnyPrioListing, PartnerFetchMeta } from '../client/portal/types.gen';
import { getAssignmentsFromOpenClaimCasesService } from '../services/portalServices';

type AssignmentsContextProps = {
  assignments: AnyPrioListing[];
  partnerMeta: PartnerFetchMeta[];
  isLoading: boolean;
  refetch: () => void;
};

type AssignmentsProviderProps = {
  children: JSX.Element | JSX.Element[];
};

const AssignmentsContext = createContext<AssignmentsContextProps | null>(null);

const AssignmentsProvider = ({ children }: AssignmentsProviderProps) => {
  const [assignments, setAssignments] = useState<AnyPrioListing[]>([]);
  const [partnerMeta, setPartnerMeta] = useState<PartnerFetchMeta[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchAssignments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAssignmentsFromOpenClaimCasesService();
      setAssignments(response.assignments);
      setPartnerMeta(response.partnerMeta);
    } catch (error) {
      console.error('Could not fetch assignments:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  return (
    <AssignmentsContext.Provider value={{ assignments, partnerMeta, isLoading, refetch: fetchAssignments }}>
      {children}
    </AssignmentsContext.Provider>
  );
};

const useAssignments = () =>
  useContext(AssignmentsContext) as AssignmentsContextProps;

export { AssignmentsProvider, useAssignments };
